import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { wrapProjects } from '../data/projects'
import { useLocation, useNavigate } from 'react-router-dom'
import { WrapProject } from '../types'

type Category = 'Vehicle Wraps' | 'Brand Identity' | 'Web Design'

export function Gallery() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [hoveredProject, setHoveredProject] = useState<number | null>(null)
  const [openCategories, setOpenCategories] = useState<Category[]>([])
  const [expandedProject, setExpandedProject] = useState<WrapProject | null>(null)
  const [galleryIndex, setGalleryIndex] = useState(0)
  const location = useLocation()
  const navigate = useNavigate()

  const handleProjectClick = (project: WrapProject) => {
    if (project.externalUrl) {
      window.open(project.externalUrl, '_blank', 'noopener,noreferrer')
    } else if (project.isCaseStudy && project.caseStudySlug) {
      navigate(`/case-studies/${project.caseStudySlug}`)
    } else if (project.images.length > 1) {
      setExpandedProject(project)
      setGalleryIndex(0)
    } else {
      setSelectedImage(project.images[0])
    }
  }

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!expandedProject) return

      if (e.key === 'Escape') {
        setExpandedProject(null)
      } else if (e.key === 'ArrowLeft') {
        setGalleryIndex(prev => prev === 0 ? expandedProject.images.length - 1 : prev - 1)
      } else if (e.key === 'ArrowRight') {
        setGalleryIndex(prev => prev === expandedProject.images.length - 1 ? 0 : prev + 1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [expandedProject])

  // Auto-open category from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash) {
      const categoryMap: { [key: string]: Category } = {
        'vehicle-wraps': 'Vehicle Wraps',
        'brand-identity': 'Brand Identity',
        'web-design': 'Web Design',
      }
      const category = categoryMap[hash]
      if (category && !openCategories.includes(category)) {
        setOpenCategories([category])
        // Scroll to section after a delay
        setTimeout(() => {
          const element = document.getElementById(hash)
          if (element) {
            const offset = 150
            const elementPosition = element.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - offset
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            })
          }
        }, 300)
      }
    }
  }, [location.hash])

  // Get projects by category
  const vehicleWraps = wrapProjects.filter(p => p.category === 'Vehicle Wraps')
  const brandIdentity = wrapProjects.filter(p => p.category === 'Brand Identity')
  const webDesign = wrapProjects.filter(p => p.category === 'Web Design')

  const toggleSection = (category: Category) => {
    if (openCategories.includes(category)) {
      setOpenCategories(openCategories.filter(c => c !== category))
    } else {
      setOpenCategories([...openCategories, category])
      // Scroll to the section after a brief delay to allow animation to start
      setTimeout(() => {
        const sectionId = category.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-')
        const element = document.getElementById(sectionId)
        if (element) {
          const offset = 150
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  }

  return (
    <div className="min-h-screen bg-background text-primary">
      {/* Hero */}
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.h1
            className="text-6xl md:text-8xl font-display font-medium tracking-tighter text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            Work
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-neutral-400 font-light max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            Business-focused creative services designed for impact.
          </motion.p>
        </div>
      </section>

      {/* Interactive Category Cards */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {/* Vehicle Wraps Card */}
            <motion.button
              onClick={() => toggleSection('Vehicle Wraps')}
              className={`group relative h-96 w-full text-left transition-all duration-500 ${openCategories.includes('Vehicle Wraps')
                  ? 'bg-surfaceHighlight'
                  : 'bg-background hover:bg-surfaceHighlight/50'
                }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="h-full flex flex-col justify-between p-10">
                <div className="text-sm font-mono text-neutral-500">01</div>
                <div>
                  <h3 className={`text-4xl font-display font-medium mb-4 transition-colors ${openCategories.includes('Vehicle Wraps') ? 'text-accent' : 'text-white'
                    }`}>Vehicle Wraps</h3>
                  <p className="font-light text-neutral-400 mb-8">Turn your fleet into mobile billboards</p>
                  <div className={`inline-flex items-center text-sm font-bold uppercase tracking-wider gap-2 group-hover:gap-4 transition-all ${openCategories.includes('Vehicle Wraps') ? 'text-white' : 'text-neutral-500 group-hover:text-white'
                    }`}>
                    Explore
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            </motion.button>

            {/* Brand Identity Card */}
            <motion.button
              onClick={() => toggleSection('Brand Identity')}
              className={`group relative h-96 w-full text-left transition-all duration-500 ${openCategories.includes('Brand Identity')
                  ? 'bg-surfaceHighlight'
                  : 'bg-background hover:bg-surfaceHighlight/50'
                }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="h-full flex flex-col justify-between p-10">
                <div className="text-sm font-mono text-neutral-500">02</div>
                <div>
                  <h3 className={`text-4xl font-display font-medium mb-4 transition-colors ${openCategories.includes('Brand Identity') ? 'text-accent' : 'text-white'
                    }`}>Brand Identity</h3>
                  <p className="font-light text-neutral-400 mb-8">Complete brand systems and visual identity</p>
                  <div className={`inline-flex items-center text-sm font-bold uppercase tracking-wider gap-2 group-hover:gap-4 transition-all ${openCategories.includes('Brand Identity') ? 'text-white' : 'text-neutral-500 group-hover:text-white'
                    }`}>
                    Explore
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            </motion.button>

            {/* Web Design Card */}
            <motion.button
              onClick={() => toggleSection('Web Design')}
              className={`group relative h-96 w-full text-left transition-all duration-500 ${openCategories.includes('Web Design')
                  ? 'bg-surfaceHighlight'
                  : 'bg-background hover:bg-surfaceHighlight/50'
                }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="h-full flex flex-col justify-between p-10">
                <div className="text-sm font-mono text-neutral-500">03</div>
                <div>
                  <h3 className={`text-4xl font-display font-medium mb-4 transition-colors ${openCategories.includes('Web Design') ? 'text-accent' : 'text-white'
                    }`}>Web Design</h3>
                  <p className="font-light text-neutral-400 mb-8">Custom websites built for performance</p>
                  <div className={`inline-flex items-center text-sm font-bold uppercase tracking-wider gap-2 group-hover:gap-4 transition-all ${openCategories.includes('Web Design') ? 'text-white' : 'text-neutral-500 group-hover:text-white'
                    }`}>
                    Explore
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-32 px-6">
        <div className="max-w-7xl mx-auto space-y-20">

          {/* Vehicle Wraps */}
          <AnimatePresence>
            {openCategories.includes('Vehicle Wraps') && vehicleWraps.length > 0 && (
              <motion.div
                id="vehicle-wraps"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <div className="border-t border-white/10 pt-12">
                  <h2 className="text-3xl font-display font-medium text-white mb-8">Vehicle Wraps</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vehicleWraps.map((project, index) => (
                      <motion.div
                        key={project.id}
                        className="group relative bg-neutral-900 overflow-hidden border border-white/5 hover:border-accent/50 transition-all cursor-pointer"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.05 }}
                        viewport={{ once: true }}
                        onClick={() => project.isCaseStudy ? window.location.href = "/case-studies/smoove-move" : setSelectedImage(project.images[0])}
                      >
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={project.images[0]}
                            alt={project.title}
                            className={`w-full h-full ${project.images[0].includes('logo') || project.images[0].includes('icon') ? 'object-contain p-8' : 'object-cover group-hover:scale-105'} transition-transform duration-700 ease-out`}
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                          <h3 className="font-medium text-white mb-1">{project.title}</h3>
                          <p className="text-xs text-neutral-400">{project.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Brand Identity */}
          <AnimatePresence>
            {openCategories.includes('Brand Identity') && (
              <motion.div
                id="brand-identity"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <div className="border-t border-white/10 pt-12">
                  <h2 className="text-3xl font-display font-medium text-white mb-8">Brand Identity</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {brandIdentity.map((project, index) => (
                      <motion.div
                        key={project.id}
                        className="group relative bg-neutral-900 overflow-hidden border border-white/5 hover:border-accent/50 transition-all cursor-pointer"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.05 }}
                        viewport={{ once: true }}
                        onMouseEnter={() => setHoveredProject(project.id)}
                        onMouseLeave={() => setHoveredProject(null)}
                        onClick={() => handleProjectClick(project)}
                      >
                        <div className="aspect-square overflow-hidden">
                          <img
                            src={hoveredProject === project.id && project.images.length > 1 ? project.images[1] : project.images[0]}
                            alt={project.title}
                            className={`w-full h-full object-contain p-8 transition-all duration-500 ${project.images[0].includes('tlg4') ? 'invert' : ''} group-hover:scale-105`}
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                          <h3 className="font-medium text-white mb-1">{project.title}</h3>
                          <p className="text-xs text-neutral-400">{project.description}</p>
                          {project.isCaseStudy && (
                            <span className="inline-flex items-center gap-1 text-xs text-accent mt-2">
                              View Case Study
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                              </svg>
                            </span>
                          )}
                          {!project.isCaseStudy && project.images.length > 1 && (
                            <span className="inline-flex items-center gap-1 text-xs text-neutral-300 mt-2">
                              View Gallery ({project.images.length} images)
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Web Design */}
          <AnimatePresence>
            {openCategories.includes('Web Design') && (
              <motion.div
                id="web-design"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <div className="border-t border-white/10 pt-12">
                  <h2 className="text-3xl font-display font-medium text-white mb-8">Web Design</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {webDesign.map((project, index) => (
                      <motion.div
                        key={project.id}
                        className="group relative bg-neutral-900 overflow-hidden border border-white/5 hover:border-accent/50 transition-all cursor-pointer"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.05 }}
                        viewport={{ once: true }}
                        onClick={() => handleProjectClick(project)}
                      >
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={project.images[0]}
                            alt={project.title}
                            className="w-full h-full object-contain p-8 transition-transform duration-700 ease-out group-hover:scale-105"
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                          <h3 className="font-medium text-white mb-1">{project.title}</h3>
                          <p className="text-xs text-neutral-400">{project.description}</p>
                          {project.externalUrl && (
                            <span className="inline-flex items-center gap-1 text-xs text-accent mt-2">
                              Visit Website
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </section>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/95 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="relative max-w-7xl w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt="Full size"
                className="w-full h-auto max-h-[90vh] object-contain shadow-2xl"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded Gallery Modal */}
      <AnimatePresence>
        {expandedProject && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/98 backdrop-blur-xl overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-6 bg-gradient-to-b from-black/80 to-transparent">
              <div>
                <h2 className="text-2xl font-display font-medium text-white">{expandedProject.title}</h2>
                <p className="text-sm text-neutral-400">{expandedProject.description}</p>
              </div>
              <button
                onClick={() => setExpandedProject(null)}
                className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Main Image */}
            <div className="h-full flex items-center justify-center px-20 py-24">
              <motion.img
                key={galleryIndex}
                src={expandedProject.images[galleryIndex]}
                alt={`${expandedProject.title} - ${galleryIndex + 1}`}
                className="max-w-full max-h-full object-contain"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Navigation Arrows */}
            {expandedProject.images.length > 1 && (
              <>
                <button
                  onClick={() => setGalleryIndex(prev => prev === 0 ? expandedProject.images.length - 1 : prev - 1)}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setGalleryIndex(prev => prev === expandedProject.images.length - 1 ? 0 : prev + 1)}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Thumbnail Strip */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center justify-center gap-2 overflow-x-auto pb-2">
                {expandedProject.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setGalleryIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === galleryIndex ? 'border-accent scale-110' : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-neutral-400 mt-3">
                {galleryIndex + 1} / {expandedProject.images.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-32 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-5xl md:text-7xl font-display font-medium text-white mb-8 tracking-tighter"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            Ready to accelerate?
          </motion.h2>
          <motion.p
            className="text-xl text-neutral-400 mb-12 font-light"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            Partner with us for exceptional creative solutions
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <a
              href="mailto:hello@chipahauk.com"
              className="inline-flex items-center px-10 py-5 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-white transition-all duration-300"
            >
              Get in touch
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
